import { useEffect, useState } from "react";
import { Search, Phone, Mail, ChevronRight } from "lucide-react";
import api from "../lib/api";
import MobileShell, { LocationSelect, useMobileLocation, Card, Empty, Skeleton } from "./MobileShell";
import { WifiPasscode } from "../components/WifiPasscode";

function MemberCard({ m, onBack }) {
  return (
    <div className="space-y-4" data-testid="m-member-detail">
      <button onClick={onBack} className="text-sm text-brand-muted" data-testid="m-member-back">← All results</button>
      <Card className="p-5 space-y-1">
        <h2 className="font-serif text-2xl">{m.company_name}</h2>
        <p className="text-sm text-brand-muted">{[m.locations?.join(", "), m.company_type, m.status === "notice" ? "On notice" : null].filter(Boolean).join(" · ")}</p>
        {m.offices?.length > 0 && <p className="text-sm">Office {m.offices.map((o) => o.number || o.name || o).join(", ")}</p>}
        {m.contract_end && <p className="text-xs text-brand-tertiary">Contract ends {new Date(`${m.contract_end}T12:00:00`).toLocaleDateString()}</p>}
      </Card>
      <div className="space-y-3">
        {(m.contacts || []).filter((c) => c.status !== "Inactive").map((c) => (
          <Card key={c.id || c.email || c.name} className="p-4 space-y-3" data-testid={`m-contact-${c.id}`}>
            <p className="text-base font-medium">{c.name}{c.primary && <span className="ml-2 text-[10px] uppercase tracking-wider text-brand-gold">Primary</span>}</p>
            <div className="flex gap-2">
              {c.phone && <a href={`tel:${c.phone}`} className="flex-1 inline-flex items-center justify-center gap-2 border border-brand-border rounded-xl py-3 text-sm active:bg-brand-hover"><Phone className="h-4 w-4" /> Call</a>}
              {c.email && <a href={`mailto:${c.email}`} className="flex-1 inline-flex items-center justify-center gap-2 border border-brand-border rounded-xl py-3 text-sm active:bg-brand-hover"><Mail className="h-4 w-4" /> Email</a>}
            </div>
            {c.email && <div className="flex text-sm"><WifiPasscode companyId={m.company_id} email={c.email} /></div>}
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function MMembers() {
  const { location } = useMobileLocation();
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setList(null); return; }
    setList(undefined);
    const t = setTimeout(() => {
      api.get("/members", { params: { q: q.trim(), location: location || "all", status: "all" } })
        .then(({ data }) => { setList(data.members || []); setError(null); })
        .catch((e) => setError(e.userMessage));
    }, 250);
    return () => clearTimeout(t);
  }, [q, location]);
  const openMember = (m) => api.get(`/members/${m.company_id}`).then(({ data }) => setOpen(data)).catch((e) => setError(e.userMessage));
  return (
    <MobileShell title="Find a member">
      {open ? <MemberCard m={open} onBack={() => setOpen(null)} /> : (
        <>
          <LocationSelect />
          <label className="relative block">
            <Search className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-brand-tertiary" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, company or office #" data-testid="m-members-search"
              className="w-full bg-white border border-brand-border rounded-xl pl-10 pr-4 py-3.5 text-base focus:outline-none focus:border-brand-ink" />
          </label>
          {error && <p className="text-sm text-brand-neg" data-testid="m-members-error">{error}</p>}
          {list === undefined ? <Skeleton /> : list === null ? <Empty>Type at least 2 letters to search.</Empty>
            : !list.length ? <Empty data-testid="m-members-empty">No current members match “{q}”.</Empty>
            : (
              <Card className="divide-y divide-brand-border overflow-hidden" data-testid="m-members-list">
                {list.slice(0, 40).map((m) => (
                  <button key={m.company_id} onClick={() => openMember(m)} className="w-full text-left flex items-center gap-3 px-4 py-3.5 active:bg-brand-hover" data-testid={`m-member-${m.company_id}`}>
                    <span className="flex-1 min-w-0">
                      <span className="block text-base truncate">{m.company_name}</span>
                      <span className="block text-xs text-brand-tertiary truncate">{[m.locations?.join(", "), m.primary_contact?.name].filter(Boolean).join(" · ")}</span>
                    </span>
                    <ChevronRight className="h-5 w-5 text-brand-tertiary" />
                  </button>
                ))}
              </Card>
            )}
          {list === undefined && null}
        </>
      )}
    </MobileShell>
  );
}
